import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import { CssBaseline, Box, CircularProgress, Container } from '@mui/material';
import { theme } from './theme/theme';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import BottomNavigation from './components/BottomNavigation';
import HomePage from './components/HomePage';
import StatsPage from './components/StatsPage';
import { ExerciseInfo } from './components/ExerciseInfo';
import { StatDetail } from './components/StatDetail';
import { WeeklyDetailPage } from './components/WeeklyDetailPage';
import { LoginPage } from './components/LoginPage';
import { UserProfile } from './components/UserProfile';
import { UpdateNotification } from './components/UpdateNotification';
import './App.css';

function AppContent() {
  const { user, loading } = useAuth();
  const location = useLocation();
  const isExerciseInfoPage = location.pathname.startsWith('/exercise/');
  const isStatDetailPage = location.pathname.startsWith('/stat/');
  const isWeeklyDetailPage = location.pathname.startsWith('/week/');

  // Show loading spinner while checking authentication
  if (loading) {
    return (
      <Box sx={{ 
        minHeight: ['100vh', '100dvh'], // Fallback for older browsers
        bgcolor: 'background.default',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 9999,
      }}>
        <Container maxWidth="sm" sx={{ textAlign: 'center' }}>
          <CircularProgress size={60} />
          <Box sx={{ mt: 2 }}>
            Loading your workout data...
          </Box>
        </Container>
      </Box>
    );
  }

  // Show login page if user is not authenticated
  if (!user) {
    return (
      <Box sx={{
        minHeight: ['100vh', '100dvh'],
        bgcolor: 'background.default',
      }}>
        <LoginPage />
      </Box>
    );
  }

  return (
    <Box 
      data-has-header={isExerciseInfoPage || isStatDetailPage || isWeeklyDetailPage}
      sx={{ 
        minHeight: ['100vh', '100dvh'], // Dynamic viewport height for mobile
        bgcolor: 'background.default',
        pb: (isExerciseInfoPage || isStatDetailPage || isWeeklyDetailPage) ? 0 : 8, // Add bottom padding for navigation
        // Remove padding-top that interferes with fixed header positioning
        pt: (isExerciseInfoPage || isStatDetailPage || isWeeklyDetailPage) 
          ? 0
          : 'env(safe-area-inset-top, 0px)',
        // Ensure main content stays below the header
        position: 'relative',
        zIndex: 1, // Lower than header z-index (1100)
      }}
    >
      {/* Main Content */}
      <Box 
        component="main" 
        className="main-content"
        sx={{ 
          // Allow natural document scrolling instead of container scrolling
          overflow: 'visible',
          position: 'relative',
          zIndex: 1, // Lower than header
        }}
      >
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/stats" element={<StatsPage />} />
          <Route path="/exercise/:exerciseIndex" element={<ExerciseInfo />} />
          <Route path="/stat/:statType" element={<StatDetail />} />
          <Route path="/week/:weekNumber" element={<WeeklyDetailPage />} />
          <Route path="/profile" element={<UserProfile />} />
        </Routes>
      </Box>
      
      {/* Bottom Navigation - Hide on exercise info, stat detail, and weekly detail pages */}
      {!isExerciseInfoPage && !isStatDetailPage && !isWeeklyDetailPage && <BottomNavigation />}
    </Box>
  );
}

function App() {
  // Expose emergency reset function to window for manual recovery
  useEffect(() => {
    (window as any).emergencyReset = () => {
      console.log('🚨 Emergency reset triggered...');
      try {
        localStorage.clear();
        sessionStorage.clear();
        console.log('✅ Emergency reset complete. Reloading page...');
        window.location.reload();
      } catch (error) {
        console.error('❌ Emergency reset failed:', error);
      }
    };
    
    console.log('💡 Emergency reset available: call emergencyReset() in console');
  }, []);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <Router>
          <UpdateNotification />
          <AppContent />
        </Router>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
